import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Calendar, 
  Clock, 
  Users, 
  CheckCircle, 
  AlertCircle, 
  TrendingUp, 
  CheckSquare,
  Smartphone,
  ChevronRight
} from "lucide-react";
import { useClients } from "@/hooks/useClients";
import { useClientStatuses } from "@/hooks/useClientStatuses";

export const MobileStatusBar = () => {
  const { data: clients = [] } = useClients();
  const { data: clientStatuses = [] } = useClientStatuses();
  const currentTime = new Date();

  // Calculate client statistics (only for statuses that should show in top nav)
  const clientStats = clientStatuses
    .filter(status => status.show_in_top_nav) // فقط الحالات المضبوطة لتظهر في القائمة العلوية
    .map(status => ({
      ...status,
      count: clients?.filter(client => client.status === status.status_key).length || 0
    }));

  const StatusSummary = () => (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-medium text-slate-800">
          {currentTime.toLocaleDateString('ar-SA', { 
            day: 'numeric', 
            month: 'short' 
          })}
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        {clientStats.slice(0, 2).map((status) => (
          status.count > 0 && (
            <Badge 
              key={status.id}
              variant="secondary" 
              className="text-xs text-white"
              style={{ backgroundColor: status.color }}
            >
              {status.count} {status.name_ar}
            </Badge>
          )
        ))}
        
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </div>
  );

  const DetailedStatus = () => (
    <div className="space-y-4">
      {/* Today's Date */}
      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
        <Calendar className="w-5 h-5 text-blue-600" />
        <div>
          <div className="font-medium text-blue-800">
            {currentTime.toLocaleDateString('ar-SA', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
          <div className="text-sm text-blue-600 mt-1">
            {currentTime.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>
      </div>

      {/* Client Status Summary */}
      <div className="grid grid-cols-1 gap-3">
        {clientStats.map((status) => (
          <div key={status.id} className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: `${status.color}20` }}>
            <Users className="w-5 h-5" style={{ color: status.color }} />
            <div className="flex-1">
              <div className="text-sm font-medium" style={{ color: status.color }}>
                {status.name_ar}
              </div>
              <div className="text-xs opacity-70" style={{ color: status.color }}>
                {status.name_en}
              </div>
            </div>
            <div className="text-lg font-bold" style={{ color: status.color }}>
              {status.count}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="top-status-bar-mobile bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200 p-3">
      <Dialog>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            className="w-full p-0 h-auto hover:bg-white/50 rounded-lg"
          >
            <StatusSummary />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-sm mx-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              ملخص اليوم
            </DialogTitle>
          </DialogHeader>
          <DetailedStatus />
        </DialogContent>
      </Dialog>
    </div>
  );
};